import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ClockIcon,
  FireIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  EyeSlashIcon,
  HeartIcon,
  ShareIcon,
  ArrowTopRightOnSquareIcon,
  ChartBarIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';

interface BidHistory {
  id: string;
  bidder: {
    address: string;
    avatar: string;
  };
  amount: number;
  timestamp: Date;
}

const AuctionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [isLiked, setIsLiked] = useState(false);
  const [bidAmount, setBidAmount] = useState('');
  const [activeTab, setActiveTab] = useState<'bids' | 'history' | 'details'>('bids');
  const [timeLeft, setTimeLeft] = useState({ hours: 2, minutes: 34, seconds: 12 });
  const [showBidModal, setShowBidModal] = useState(false);

  // Map de items por id (dashboard mock data)
  const itemMap: Record<string, { title: string; image: string; collection: string }> = {
    '1': { title: 'Equus Dualis II #7',   image: '/Equus Dualis/Equus Dualis II ) 7.png',   collection: 'Equus Dualis II' },
    '2': { title: 'Warrior Mythology #12', image: '/warrior mythology/warrior mythology12.jpg', collection: 'Warrior Mythology' },
    '3': { title: 'Equus Dualis II #3',   image: '/Equus Dualis/Equus Dualis II ) 3.png',   collection: 'Equus Dualis II' },
    '4': { title: 'Warrior Mythology #35', image: '/warrior mythology/warrior mythology35.jpg', collection: 'Warrior Mythology' },
    '5': { title: 'Equus Dualis II #5',   image: '/Equus Dualis/Equus Dualis II ) 5.png',   collection: 'Equus Dualis II' },
    '6': { title: 'Warrior Mythology #17', image: '/warrior mythology/warrior mythology17.jpg', collection: 'Warrior Mythology' },
    '7': { title: 'Equus Dualis II #18',  image: '/Equus Dualis/Equus Dualis II ) 18.png',  collection: 'Equus Dualis II' },
    '8': { title: 'Warrior Mythology #43', image: '/warrior mythology/warrior mythology43.jpg', collection: 'Warrior Mythology' },
  };
  const itemData = itemMap[id || ''] || {
    title: 'NARDIHA NFT',
    image: '/Equus Dualis/Equus Dualis II ) 1.png',
    collection: 'NARDIHA Arts',
  };

  // Mock auction data
  const auction = {
    id: id,
    title: itemData.title,
    description: 'Una pieza única de la colección NARDIHA Arts, disponible en subasta descentralizada en TEMPLUM DAO. Este NFT otorga acceso exclusivo a futuros drops y derechos de voto en la DAO.',
    image: itemData.image,
    currentBid: 12.5,
    startingPrice: 5,
    reservePrice: 10,
    minBidIncrement: 0.1,
    endTime: new Date(Date.now() + 3600000 * 2.5),
    auctionType: 'ENGLISH' as 'ENGLISH' | 'DUTCH' | 'VICKREY' | 'CANDLE',
    bidCount: 23,
    viewCount: 1542,
    likeCount: 89,
    creator: {
      name: 'NARDIHA Arts',
      address: '0x1234...5678',
      avatar: '/Equus Dualis/Equus Dualis II ) 2.png',
      verified: true,
    },
    collection: {
      name: itemData.collection,
      floorPrice: 8.2,
      verified: true,
    },
    owner: {
      name: 'NARDIHA Holdings',
      address: '0x8765...4321',
      avatar: '/warrior mythology/warrior mythology1.jpg',
    },
    attributes: [
      { trait_type: 'Background', value: 'Nebula', rarity: 12 },
      { trait_type: 'Body', value: 'Cosmic', rarity: 8 },
      { trait_type: 'Eyes', value: 'Galaxy', rarity: 3 },
      { trait_type: 'Accessory', value: 'Star Crown', rarity: 1 },
    ],
    royalty: 7.5,
    tokenId: '2847',
    contractAddress: '0xABCD...EFGH',
    blockchain: 'Ethereum',
  };

  const bidHistory: BidHistory[] = [
    { id: '1', bidder: { address: '0xABCD...1234', avatar: 'https://picsum.photos/seed/bidder1/100/100' }, amount: 12.5, timestamp: new Date(Date.now() - 300000) },
    { id: '2', bidder: { address: '0xDEFG...5678', avatar: 'https://picsum.photos/seed/bidder2/100/100' }, amount: 11.2, timestamp: new Date(Date.now() - 900000) },
    { id: '3', bidder: { address: '0xHIJK...9012', avatar: 'https://picsum.photos/seed/bidder3/100/100' }, amount: 10.0, timestamp: new Date(Date.now() - 1800000) },
    { id: '4', bidder: { address: '0xLMNO...3456', avatar: 'https://picsum.photos/seed/bidder4/100/100' }, amount: 8.5, timestamp: new Date(Date.now() - 3600000) },
    { id: '5', bidder: { address: '0xPQRS...7890', avatar: 'https://picsum.photos/seed/bidder5/100/100' }, amount: 7.0, timestamp: new Date(Date.now() - 7200000) },
  ];

  const auctionTypeConfig = {
    ENGLISH: {
      icon: ArrowTrendingUpIcon,
      label: 'English Auction',
      description: 'Price increases with each bid. Highest bidder wins.',
      color: 'from-green-500 to-emerald-600',
      bgColor: 'bg-green-500/10',
      textColor: 'text-green-400',
    },
    DUTCH: {
      icon: ArrowTrendingDownIcon,
      label: 'Dutch Auction',
      description: 'Price decreases over time. First bid wins.',
      color: 'from-orange-500 to-red-600',
      bgColor: 'bg-orange-500/10',
      textColor: 'text-orange-400',
    },
    VICKREY: {
      icon: EyeSlashIcon,
      label: 'Vickrey Auction',
      description: 'Sealed bids. Winner pays second-highest price.',
      color: 'from-purple-500 to-violet-600',
      bgColor: 'bg-purple-500/10',
      textColor: 'text-purple-400',
    },
    CANDLE: {
      icon: FireIcon,
      label: 'Candle Auction',
      description: 'Random end time. Discourages last-second bidding.',
      color: 'from-amber-500 to-orange-600',
      bgColor: 'bg-amber-500/10',
      textColor: 'text-amber-400',
    },
  };

  const typeConfig = auctionTypeConfig[auction.auctionType];
  const TypeIcon = typeConfig.icon;

  // Countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      const diff = auction.endTime.getTime() - Date.now();
      if (diff <= 0) {
        setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      setTimeLeft({
        hours: Math.floor(diff / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [auction.endTime]);

  const formatTimeAgo = (date: Date) => {
    const diff = Date.now() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  const minBid = auction.currentBid + auction.minBidIncrement;

  return (
    <div className="min-h-screen py-8">
      <div className="container-custom">
        {/* Breadcrumb */}
        <nav className="flex items-center space-x-2 text-sm text-gray-400 mb-8">
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <span>/</span>
          <Link to="/auctions" className="hover:text-white transition-colors">Auctions</Link>
          <span>/</span>
          <span className="text-white">{auction.title}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Left Column - Image */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-4"
          >
            {/* Main Image */}
            <div className="relative aspect-square rounded-2xl overflow-hidden glass-card p-2">
              <img
                src={auction.image}
                alt={auction.title}
                className="w-full h-full object-cover rounded-xl"
              />

              {/* Auction Type Badge */}
              <div className={`absolute top-6 left-6 flex items-center space-x-2 px-4 py-2 rounded-full ${typeConfig.bgColor} backdrop-blur-sm`}>
                <TypeIcon className={`h-5 w-5 ${typeConfig.textColor}`} />
                <span className={`font-medium ${typeConfig.textColor}`}>{typeConfig.label}</span>
              </div>

              {/* Actions */}
              <div className="absolute top-6 right-6 flex items-center space-x-2">
                <button
                  onClick={() => setIsLiked(!isLiked)}
                  className="p-3 rounded-full bg-dark-900/60 backdrop-blur-sm hover:bg-dark-800 transition-colors"
                >
                  {isLiked ? (
                    <HeartSolidIcon className="h-5 w-5 text-red-500" />
                  ) : (
                    <HeartIcon className="h-5 w-5 text-white" />
                  )}
                </button>
                <button className="p-3 rounded-full bg-dark-900/60 backdrop-blur-sm hover:bg-dark-800 transition-colors">
                  <ShareIcon className="h-5 w-5 text-white" />
                </button>
              </div>
            </div>

            {/* Description */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold mb-3">Description</h3>
              <p className="text-gray-400">{auction.description}</p>
            </div>

            {/* Attributes */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold mb-4">Attributes</h3>
              <div className="grid grid-cols-2 gap-3">
                {auction.attributes.map((attr, index) => (
                  <div
                    key={index}
                    className="p-3 rounded-xl bg-primary-500/5 border border-primary-500/20"
                  >
                    <p className="text-xs text-primary-400 uppercase mb-1">{attr.trait_type}</p>
                    <p className="font-semibold">{attr.value}</p>
                    <p className="text-xs text-gray-500">{attr.rarity}% have this</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Details */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold mb-4">Details</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-white/5">
                  <span className="text-gray-400">Contract Address</span>
                  <a href="#" className="flex items-center space-x-1 text-primary-400 hover:text-primary-300">
                    <span>{auction.contractAddress}</span>
                    <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                  </a>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-white/5">
                  <span className="text-gray-400">Token ID</span>
                  <span className="font-mono">{auction.tokenId}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-white/5">
                  <span className="text-gray-400">Blockchain</span>
                  <span>{auction.blockchain}</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-gray-400">Creator Royalty</span>
                  <span>{auction.royalty}%</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column - Info & Bidding */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            {/* Collection & Title */}
            <div>
              <Link
                to={`/collections/${auction.collection.name}`}
                className="inline-flex items-center space-x-2 text-primary-400 hover:text-primary-300 mb-2"
              >
                <span>{auction.collection.name}</span>
                {auction.collection.verified && (
                  <ShieldCheckIcon className="h-4 w-4" />
                )}
              </Link>
              <h1 className="text-3xl md:text-4xl font-bold">{auction.title}</h1>
            </div>

            {/* Creator & Owner */}
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-3">
                <img
                  src={auction.creator.avatar}
                  alt={auction.creator.name}
                  className="w-10 h-10 rounded-full"
                />
                <div>
                  <p className="text-xs text-gray-500">Creator</p>
                  <div className="flex items-center space-x-1">
                    <span className="font-medium">{auction.creator.name}</span>
                    {auction.creator.verified && (
                      <CheckCircleIcon className="h-4 w-4 text-primary-400" />
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <img
                  src={auction.owner.avatar}
                  alt={auction.owner.name}
                  className="w-10 h-10 rounded-full"
                />
                <div>
                  <p className="text-xs text-gray-500">Owner</p>
                  <span className="font-medium">{auction.owner.name}</span>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="flex items-center space-x-6 text-sm">
              <div className="flex items-center space-x-1 text-gray-400">
                <ChartBarIcon className="h-4 w-4" />
                <span>{auction.viewCount} views</span>
              </div>
              <div className="flex items-center space-x-1 text-gray-400">
                <HeartIcon className="h-4 w-4" />
                <span>{auction.likeCount} likes</span>
              </div>
              <div className="flex items-center space-x-1 text-gray-400">
                <UserGroupIcon className="h-4 w-4" />
                <span>{auction.bidCount} bids</span>
              </div>
            </div>

            {/* Auction Type Info */}
            <div className={`p-4 rounded-xl ${typeConfig.bgColor} border border-white/10`}>
              <div className="flex items-start space-x-3">
                <TypeIcon className={`h-6 w-6 ${typeConfig.textColor} flex-shrink-0 mt-0.5`} />
                <div>
                  <p className={`font-semibold ${typeConfig.textColor}`}>{typeConfig.label}</p>
                  <p className="text-sm text-gray-400">{typeConfig.description}</p>
                </div>
              </div>
            </div>

            {/* Main Bidding Card */}
            <div className="glass-card p-6">
              {/* Countdown */}
              <div className="mb-6">
                <p className="text-sm text-gray-400 mb-2">Auction ends in</p>
                <div className="flex items-center space-x-4">
                  {[
                    { value: timeLeft.hours, label: 'Hours' },
                    { value: timeLeft.minutes, label: 'Minutes' },
                    { value: timeLeft.seconds, label: 'Seconds' },
                  ].map((item, index) => (
                    <React.Fragment key={item.label}>
                      <div className="text-center">
                        <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary-400 to-accent-400 bg-clip-text text-transparent">
                          {item.value.toString().padStart(2, '0')}
                        </div>
                        <p className="text-xs text-gray-500">{item.label}</p>
                      </div>
                      {index < 2 && <span className="text-2xl text-gray-600">:</span>}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* Current Bid */}
              <div className="mb-6 p-4 rounded-xl bg-dark-700/50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-400">Current Bid</span>
                  <span className="text-sm text-gray-500">
                    Reserve {auction.currentBid >= auction.reservePrice ? 'met' : 'not met'}
                  </span>
                </div>
                <div className="flex items-baseline space-x-2">
                  <span className="text-3xl font-bold">{auction.currentBid} ETH</span>
                  <span className="text-gray-400">($24,890)</span>
                </div>
              </div>

              {/* Bid Input */}
              <div className="mb-4">
                <label className="block text-sm text-gray-400 mb-2">
                  Your Bid (Min: {minBid.toFixed(2)} ETH)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={bidAmount}
                    onChange={(e) => setBidAmount(e.target.value)}
                    placeholder={minBid.toFixed(2)}
                    step="0.01"
                    className="w-full px-4 py-4 pr-16 bg-dark-700 border border-white/10 rounded-xl focus:outline-none focus:border-primary-500 text-xl font-semibold"
                  />
                  <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 font-medium">
                    ETH
                  </span>
                </div>
              </div>

              {/* Quick Bid Buttons */}
              <div className="flex items-center space-x-2 mb-6">
                {[minBid, minBid + 0.5, minBid + 1].map((amount) => (
                  <button
                    key={amount}
                    onClick={() => setBidAmount(amount.toFixed(2))}
                    className="flex-1 px-3 py-2 bg-dark-700 border border-white/10 rounded-lg text-sm hover:border-primary-500 transition-colors"
                  >
                    {amount.toFixed(2)} ETH
                  </button>
                ))}
              </div>

              {/* Place Bid Button */}
              <button className="w-full btn-primary py-4 text-lg mb-4">
                Place Bid
              </button>

              {/* Info */}
              <div className="flex items-start space-x-2 text-sm text-gray-400">
                <InformationCircleIcon className="h-5 w-5 flex-shrink-0" />
                <p>By placing a bid, you agree to the terms of service. Your bid will be locked until outbid or auction ends.</p>
              </div>
            </div>

            {/* Tabs */}
            <div className="glass-card overflow-hidden">
              {/* Tab Headers */}
              <div className="flex border-b border-white/10">
                {[
                  { id: 'bids', label: 'Bid History', count: bidHistory.length },
                  { id: 'history', label: 'Activity' },
                  { id: 'details', label: 'Price History' },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`flex-1 px-4 py-4 text-sm font-medium transition-colors relative ${
                      activeTab === tab.id
                        ? 'text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    {tab.label}
                    {tab.count && (
                      <span className="ml-1 text-gray-500">({tab.count})</span>
                    )}
                    {activeTab === tab.id && (
                      <motion.div
                        layoutId="activeTab"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-500 to-accent-500"
                      />
                    )}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="p-4 max-h-80 overflow-y-auto">
                <AnimatePresence mode="wait">
                  {activeTab === 'bids' && (
                    <motion.div
                      key="bids"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="space-y-3"
                    >
                      {bidHistory.map((bid, index) => (
                        <div
                          key={bid.id}
                          className={`flex items-center justify-between p-3 rounded-xl ${
                            index === 0 ? 'bg-primary-500/10 border border-primary-500/20' : 'bg-dark-700/50'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <img
                              src={bid.bidder.avatar}
                              alt=""
                              className="w-10 h-10 rounded-full"
                            />
                            <div>
                              <p className="font-medium">{bid.bidder.address}</p>
                              <p className="text-xs text-gray-500">{formatTimeAgo(bid.timestamp)}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold">{bid.amount} ETH</p>
                            {index === 0 && (
                              <span className="text-xs text-primary-400">Highest Bid</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </motion.div>
                  )}

                  {activeTab === 'history' && (
                    <motion.div
                      key="history"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="space-y-3"
                    >
                      <div className="flex items-center justify-between p-3 rounded-xl bg-dark-700/50">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 rounded-lg bg-green-500/10">
                            <ArrowTrendingUpIcon className="h-5 w-5 text-green-400" />
                          </div>
                          <div>
                            <p className="font-medium">Auction Started</p>
                            <p className="text-xs text-gray-500">2 hours ago</p>
                          </div>
                        </div>
                        <span className="text-gray-400">5 ETH</span>
                      </div>
                      <div className="flex items-center justify-between p-3 rounded-xl bg-dark-700/50">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 rounded-lg bg-blue-500/10">
                            <CheckCircleIcon className="h-5 w-5 text-blue-400" />
                          </div>
                          <div>
                            <p className="font-medium">Listed</p>
                            <p className="text-xs text-gray-500">3 hours ago</p>
                          </div>
                        </div>
                        <span className="text-gray-400">By {auction.owner.name}</span>
                      </div>
                    </motion.div>
                  )}

                  {activeTab === 'details' && (
                    <motion.div
                      key="details"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="flex items-center justify-center h-40"
                    >
                      <div className="text-center">
                        <ChartBarIcon className="h-10 w-10 text-gray-500 mx-auto mb-2" />
                        <p className="text-gray-400">Price history chart coming soon</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Warning for Candle Auctions */}
            {auction.auctionType === 'CANDLE' && (
              <div className="flex items-start space-x-3 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                <ExclamationTriangleIcon className="h-6 w-6 text-amber-400 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-amber-400">Candle Auction Warning</p>
                  <p className="text-sm text-gray-400">
                    This auction may end at any time. The exact end time is determined randomly using Chainlink VRF. Place your bids early!
                  </p>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AuctionDetailPage;
